import type {
  Customer,
  CustomerDetail,
  CustomerService,
  BillingRecord,
  PaginatedResponse,
  CustomerFilters,
} from "../../types";
import type { EndpointBuilder } from "@reduxjs/toolkit/query/react";

/**
 * Customer Query Endpoints
 * Handles read operations for customer management
 */
export const customerQueries = (builder: EndpointBuilder<any, string, string>) => ({
  /** Get paginated list of customers with optional filters */
  getCustomers: builder.query<PaginatedResponse<Customer>, CustomerFilters>({
    query: (filters = {}) => ({
      url: "admin/customers",
      params: filters,
    }),
    providesTags: (result) =>
      result
        ? [
            ...result.data.map(({ id }) => ({
              type: "Customers" as const,
              id,
            })),
            { type: "Customers", id: "LIST" },
          ]
        : [{ type: "Customers", id: "LIST" }],
  }),

  /** Get detailed customer information by ID */
  getCustomerById: builder.query<CustomerDetail, string>({
    query: (id) => `admin/customers/${id}`,
    providesTags: (_result, _error, id) => [{ type: "Customers", id }],
  }),

  /** Get services associated with a customer */
  getCustomerServices: builder.query<CustomerService[], string>({
    query: (customerId) => `admin/customers/${customerId}/services`,
    providesTags: (_result, _error, customerId) => [
      { type: "Customers", id: `${customerId}-services` },
    ],
  }),

  /** Get billing records for a customer */
  getCustomerBilling: builder.query<
    PaginatedResponse<BillingRecord>,
    {
      customerId: string;
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    }
  >({
    query: ({ customerId, page = 1, limit = 10, search, status }) => ({
      url: `admin/customers/${customerId}/billing`,
      params: {
        page,
        limit,
        ...(search && { search }),
        ...(status && { status }),
      },
    }),
    providesTags: (_result, _error, { customerId }) => [
      { type: "Customers", id: `${customerId}-billing` },
    ],
  }),
});